import { useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { Text, Link, Stack } from "@certn/cascadia";
import {
  Avatar,
  BasicButton,
  Container,
  CopyToClipboard,
  DataTable,
  Illustration,
  Layout,
  Loader,
  MediaQuery,
  NeutralTag,
  Pagination,
  SuccessTag,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { truncateMiddle } from "@certn/utils";
import { formatDate } from "@certn/utils/src/date-utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ActionsDropdown } from "./components/ActionsDropdown";
import { ApplyLinkCreatedModal } from "./components/ApplyLinkCreatedModal";
import { ApplyLinkEmptyState } from "./components/ApplyLinkEmptyState";
import { GroupFilter } from "./components/GroupFilter";
import { PackageFilter } from "./components/PackageFilter";
import { RequestorFilter } from "./components/RequestorFilter";
import { StatusFilter } from "./components/StatusFilter";
import { TableDataCard } from "./components/TableDataCard";
import { TagsFilter } from "./components/TagsFilter";
import { AppFooterLinks } from "../../components/app-footer/AppFooterLinks";
import { AppHeader } from "../../components/app-header/AppHeader";
import { AppNavigation } from "../../components/app-navigation/AppNavigation";
import { FilterBar } from "../../components/filters/FilterBar";
import { PageTitle } from "../../components/page-title/PageTitle";
import { useCanCreateApplyLinks } from "../../components/permissions/Permissions";
import { TagList } from "../../components/tag-list/TagList";
import { useQueryParams } from "../../hooks/useQueryParams";
import { queryParamsToApiParams } from "../utils";

export function ApplyLinkListView() {
  const { t } = useTranslation();
  const { params, setParam } = useQueryParams();
  const navigate = useNavigate();

  const { data, mutate, isLoading } = useClientPortalAPI(
    `/api/internal/client-portal/apply-links/`,
    queryParamsToApiParams(params)
  );
  const hasFilters = !!Object.entries(params).length;
  const { clientPortalApplyLinksFastFollow } = useFlags();

  const canCreateApplyLinks = useCanCreateApplyLinks();

  const columns: any = [
    {
      key: "package",
      name: t("Apply link details"),
      isRowHeader: true,
      width: 240,
      testID: "apply-link-details-column-header",
      render({
        package: packageDetails,
        customer_group,
        name: applyLinkName,
      }: {
        package: { name: string };
        customer_group: { name: string };
        name: string;
      }) {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Text
              style="global/body-secondary"
              color="text-base"
              testID="apply-link-name"
            >
              {applyLinkName}
            </Text>
            <Text
              style="global/info-label"
              color="text-weak"
              testID="apply-link-package-details"
            >
              {packageDetails.name} • {customer_group.name}
            </Text>
          </div>
        );
      },
    },
    {
      key: "link",
      name: t("Apply link"),
      isRowHeader: true,
      width: 180,
      testID: "apply-link-link-column-header",
      render({ link }: { link: string }) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-1)",
            }}
          >
            <CopyToClipboard
              text={truncateMiddle(link)}
              url={link}
              isHiddenUntilHover={false}
              testID="apply-link-link"
            />
          </div>
        );
      },
    },
    {
      key: "tags",
      name: t("Tags"),
      isRowHeader: true,
      width: 180,
      testID: "apply-link-tags-column-header",
      render({
        package: packageDetails,
      }: {
        package: { tags: readonly components["schemas"]["PackageListTags"][] };
      }) {
        return (
          <TagList
            tags={packageDetails.tags.map((tag) => tag.name)}
            capacity={3}
            testID="apply-link"
          />
        );
      },
    },
    {
      key: "usage",
      name: t("Usage"),
      isRowHeader: true,
      width: 80,
      testID: "apply-link-usage-column-header",
      render({ usage }: { usage: string }) {
        return (
          <Text style="form/body-secondary" testID="apply-link-usage">
            {usage}
          </Text>
        );
      },
    },
    {
      key: "is_active",
      name: t("Status"),
      isRowHeader: true,
      width: 120,
      testID: "apply-link-status-column-header",
      render({ is_active }: { is_active: boolean }) {
        return (
          <>
            {is_active ? (
              <SuccessTag testID="active-tag" type="subtle">
                {t("Active")}
              </SuccessTag>
            ) : (
              <NeutralTag type="subtle" testID="deactivated-tag">
                {t("Deactivated")}
              </NeutralTag>
            )}
          </>
        );
      },
    },
    {
      key: "created",
      name: t("Created"),
      isRowHeader: true,
      allowsSorting: true,
      width: 120,
      testID: "apply-link-created-column-header",
      render({ created }: { created: string }) {
        return (
          <Text style="form/body-secondary" testID="apply-link-created">
            {formatDate(created, "MMM d, yyyy")}
          </Text>
        );
      },
    },
    {
      key: "requestor",
      name: t("Requestor"),
      isRowHeader: true,
      width: 90,
      testID: "apply-link-requestor-column-header",
      render({ requestor }: { requestor: { auth0_full_name: string } }) {
        return (
          <Avatar name={requestor.auth0_full_name} testID="apply-link-avatar" />
        );
      },
    },
    {
      key: "actions",
      isAction: true,
      width: 48,
      render({
        is_active,
        link,
        id,
        name: applyLinkName,
      }: {
        is_active: boolean;
        link: string;
        id: string;
        name: string;
      }) {
        return (
          <ActionsDropdown
            isActive={is_active}
            link={link}
            id={id}
            mutate={mutate}
            name={applyLinkName}
          />
        );
      },
    },
  ];

  return (
    <Layout
      layout="narrow"
      header={<AppHeader />}
      navigation={<AppNavigation />}
      footer={<AppFooterLinks />}
    >
      <Container layout="full-width">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <PageTitle>{t("Apply links")}</PageTitle>
          {canCreateApplyLinks && (
            <BasicButton
              onPress={() => {
                navigate(`/apply-link/create`);
              }}
              className="client-portal--apply-link-page--create-apply-link-click-rate"
              testID="new-apply-link-button"
            >
              {t("New apply link")}
            </BasicButton>
          )}
        </div>
        <MediaQuery sm type="hide">
          <Stack orientation="horizontal" gap="space-2">
            <Text color="text-weak">
              <Trans>
                Use apply links to handle high volumes of applications
                efficiently, letting applicants complete screenings via a
                dedicated URL.
              </Trans>
            </Text>
            <Link
              target="_blank"
              href="https://portal.certn.co/hc/en-us/articles/36283795438867-Apply-links"
              textStyle="global/body-secondary"
              linkStyle="monochrome"
              testID="tasks-learn-more-link"
            >
              <Trans>Learn more</Trans>
            </Link>
          </Stack>
        </MediaQuery>
        {!isLoading && !data?.results?.length && !hasFilters ? (
          // TODO CP-1091 Add logic to show no active apply links empty state
          <ApplyLinkEmptyState
            message={t("You have no apply links yet.")}
            canCreate={canCreateApplyLinks}
          />
        ) : (
          <>
            <MediaQuery sm type="hide">
              {clientPortalApplyLinksFastFollow && (
                <FilterBar testID="apply-link-filters" hasSearch>
                  <PackageFilter />
                  <GroupFilter />
                  <TagsFilter />
                  <RequestorFilter />
                  <StatusFilter />
                </FilterBar>
              )}
              <DataTable
                columns={columns}
                rows={data?.results || []}
                isLoading={isLoading}
                aria-label={t("Apply links table")}
                renderEmptyState={() => (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Illustration name="no-results" />
                    {t("There are no results that match your current filter.")}
                  </div>
                )}
                onSortChange={(sortDescriptor) =>
                  setParam(
                    "ordering",
                    sortDescriptorToOrderingParam(sortDescriptor)
                  )
                }
                sortDescriptor={orderingParamToSortDescriptor(params.ordering)}
                testId="apply-link-list-view-data-table"
              />
            </MediaQuery>
            <MediaQuery sm type="show">
              {isLoading ? (
                <Loader />
              ) : (
                <Container>
                  {clientPortalApplyLinksFastFollow && (
                    <FilterBar testID="apply-link-filters-mobile">
                      <PackageFilter />
                      <GroupFilter />
                      <TagsFilter />
                      <RequestorFilter />
                      <StatusFilter />
                    </FilterBar>
                  )}
                  {data?.results?.map((applyLink) => {
                    return (
                      <TableDataCard
                        key={applyLink.id}
                        isActive={applyLink.is_active}
                        packageName={applyLink.package.name}
                        customerGroup={applyLink.customer_group.name}
                        applyLinkName={applyLink.name}
                        requestorName={
                          applyLink.requestor?.auth0_full_name || ""
                        }
                        tags={applyLink.package.tags}
                        link={applyLink.link}
                        usage={applyLink.usage}
                        createdDate={applyLink.created}
                        id={applyLink.id}
                        mutate={mutate}
                      />
                    );
                  })}
                </Container>
              )}
            </MediaQuery>
            <Pagination
              setPage={(page) => setParam("page", page.toString())}
              setPageCount={(pageCount) =>
                setParam("page_size", pageCount.toString())
              }
              total={data?.count ?? 0}
              page={params.page ? Number(params.page) : 1}
              pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
            />
          </>
        )}
        <ApplyLinkCreatedModal />
      </Container>
    </Layout>
  );
}
